/* eslint-disable */

import { useState } from 'react'
import { CheckCircle2, Landmark, AlertTriangle } from 'lucide-react'

import { Card, CardContent } from '../../Components/ui/card'
import { Input } from '../../Components/ui/input'
import { Button } from '../../Components/ui/button'
import { Label } from '../../Components/ui/label'
import { post } from '../../http/restservice'
import { useRecoilValueLoadable } from 'recoil'
import { UserBasketsAdmin } from '../../store/AdminStore'
import { toCurrency } from '../../utils/CurrencyUtil'
import { cn } from '../../lib/utils'

const CREATE_MANDATE_ENDPOINT = '/mutualfundservice/api/mutualFund/mandateRequestByAdmin'

const MANDATE_AMOUNT_OPTIONS = [
  { label: '1 Lac', value: '100000' },
  { label: '10 Lac', value: '1000000' },
  { label: '1 Cr', value: '10000000' },
]

const CreateMandate = ({ clientCode, embedded = false, onSuccess }) => {
  const userBasketsLoadable = useRecoilValueLoadable(UserBasketsAdmin(clientCode))
  const [amount, setAmount] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  const draftAdminPlansExist = userBasketsLoadable?.contents?.data?.data?.some(
    (basket) => basket.basketStatus === 'DRAFT' && basket.createdSource === 'ACTION PLAN',
  )

  const selectedAmountLabel =
    MANDATE_AMOUNT_OPTIONS.find((option) => option.value === amount)?.label || null

  const handleSubmit = async () => {
    setError('')
    setResult(null)

    const normalizedAmount = Number(amount)
    if (!clientCode?.trim()) {
      setError('Client code is required')
      return
    }
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      setError('Please select a valid amount')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        clientCode: clientCode.trim(),
        amount: normalizedAmount,
      }

      const response = await post(
        CREATE_MANDATE_ENDPOINT,
        payload,
        { 'Content-Type': 'application/json' },
        undefined,
      )

      if (response?.isError) {
        throw new Error(response?.response?.message || 'Failed to create mandate')
      }

      setResult(response)
      onSuccess?.(response)
    } catch (e) {
      setError(e?.message || 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={embedded ? 'w-full' : 'mx-auto w-full max-w-none px-1 pb-6 pt-2 md:px-2'}>
      {!embedded && (
        <div className='mb-4 md:mb-5'>
          <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
            Admin tools
          </p>
          <h1 className='font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
            Create Mandate
          </h1>
          <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
            Create a mandate request for this client by selecting an amount.
          </p>
        </div>
      )}

      <Card className='w-full overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:rounded-3xl'>
        <CardContent className='p-5 md:p-8'>
          {draftAdminPlansExist ? (
            <div className='mb-5 flex items-start gap-3 rounded-2xl border border-[var(--fb-warning-border)] bg-[var(--fb-warning-bg)] px-4 py-3 text-left lg:col-span-2'>
              <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-[var(--fb-warning)]' />
              <p className='text-sm text-[var(--fb-warning)]'>
                Draft Admin Plans Exist. Please clear the draft admin plans before creating a
                mandate.
              </p>
            </div>
          ) : null}

          <div className='grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8'>
            <div className='space-y-5'>
              <div className='flex flex-col items-center gap-3 text-center lg:items-start lg:text-left'>
                <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                  <Landmark className='h-7 w-7' aria-hidden='true' />
                </div>
                <div>
                  <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
                    Mandate request
                  </p>
                  <p className='mt-1 text-sm text-[var(--fb-text-secondary)]'>
                    Amount options are fixed for admin mandate creation.
                  </p>
                </div>
              </div>

              <div className='rounded-2xl border border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-5 py-6 text-center lg:text-left'>
                <div className='font-DMSans text-3xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-4xl'>
                  {amount ? `₹ ${toCurrency(amount)}` : '₹ —'}
                </div>
                <div className='mt-1 text-sm text-[var(--fb-text-muted)]'>
                  {selectedAmountLabel
                    ? `Selected mandate amount · ${selectedAmountLabel}`
                    : 'Select a mandate amount'}
                </div>
              </div>
            </div>

            <div className='space-y-4 lg:space-y-5'>
              <div className='space-y-3 rounded-2xl border border-[var(--fb-border)] p-4 md:p-5'>
                <Label
                  htmlFor='clientCode'
                  className='text-sm font-medium text-[var(--fb-text)]'
                >
                  Client Code
                </Label>
                <Input
                  disabled
                  id='clientCode'
                  value={clientCode}
                  placeholder='e.g. SSL1023'
                  autoComplete='off'
                  className='h-11 rounded-xl border-[var(--fb-border)] bg-[var(--fb-surface-muted)] font-semibold text-[var(--fb-text-strong)]'
                />
              </div>

              <div className='space-y-3 rounded-2xl border border-[var(--fb-border)] p-4 md:p-5'>
                <div className='text-sm font-medium text-[var(--fb-text)]'>Amount</div>
                <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
                  {MANDATE_AMOUNT_OPTIONS.map((option) => {
                    const isSelected = amount === option.value
                    return (
                      <button
                        key={option.value}
                        type='button'
                        onClick={() => setAmount(option.value)}
                        className={cn(
                          'fabits-text-ui rounded-xl border px-3 py-3 text-sm font-semibold transition-colors',
                          isSelected
                            ? 'border-[var(--fb-accent)] bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)] shadow-[var(--fb-shadow-sm)]'
                            : 'border-[var(--fb-border)] bg-[var(--fb-surface)] text-[var(--fb-text)] hover:bg-[var(--fb-surface-muted)]',
                        )}
                      >
                        <div>{option.label}</div>
                        <div
                          className={cn(
                            'mt-0.5 text-xs font-medium',
                            isSelected
                              ? 'text-[var(--fb-accent-text)]'
                              : 'text-[var(--fb-text-muted)]',
                          )}
                        >
                          ₹ {toCurrency(option.value)}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {error ? (
                <p className='text-center text-sm text-error-600 lg:text-left'>{error}</p>
              ) : null}

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !amount}
                className='h-12 w-full rounded-xl bg-[var(--fb-accent)] text-base font-semibold text-white hover:bg-[var(--fb-accent-hover)] disabled:bg-[var(--fb-accent)] disabled:opacity-50'
              >
                {isSubmitting ? 'Submitting...' : 'Create Mandate'}
              </Button>

              {result ? (
                <div className='space-y-3 rounded-2xl border border-[var(--fb-border)] bg-[var(--fb-positive-bg)] p-4 md:p-5'>
                  <div className='flex items-start gap-3'>
                    <CheckCircle2 className='mt-0.5 h-5 w-5 shrink-0 text-[var(--fb-positive)]' />
                    <div>
                      <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>
                        Mandate request submitted
                      </p>
                      <p className='mt-1 text-sm text-[var(--fb-text-secondary)]'>
                        Request created for {clientCode}
                        {amount ? ` · ₹ ${toCurrency(amount)}` : ''}.
                      </p>
                    </div>
                  </div>
                  <details className='rounded-xl border border-[var(--fb-border)] bg-[var(--fb-surface)] p-3'>
                    <summary className='cursor-pointer text-xs font-medium text-[var(--fb-text-muted)]'>
                      View response details
                    </summary>
                    <pre className='mt-3 max-h-40 overflow-auto whitespace-pre-wrap text-xs text-[var(--fb-text-secondary)]'>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CreateMandate
